import { Component, computed, DestroyRef, inject, LOCALE_ID, OnInit, signal } from '@angular/core';

import { displayName, optionalName } from '../../../../validators/name-rules';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { Router } from '@angular/router';

import {
  arabicOnlyValidator,
  noLeadingTrailingSpacesValidator,
  noNumbersValidator,
  englishOnlyValidator,
} from '../partially-delivered-reasons.validators';

import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';

import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';

import { Loading } from '../../../../utils/loading/loading';

import { FilterInput } from '../../../../shared/ui/shared-filters/filter-input/filter-input';

import { SubmitButton } from '../../../../shared/ui/Buttons/submit-button/submit-button';

import { PartiallyDeliveredReasonsService } from '../../../../services/partially-delivered-reasons/partially-delivered-reasons.service';

import { HasScopeDirective } from '../../../../services/scope/has-scope.directive';

import { SCOPES } from '../../../../services/scope/scope-constants';

import { PartiallyDeliveredReasonDTO } from '../../../../pages/admin-dashboard/partially-delivered-reasons/partiallyDeliveredReason.model';

@Component({
  selector: 'app-partially-delivered-reasons-edit-component',

  imports: [
    ConfirmationDialog,
    SuccessfulNotification,
    Loading,
    FilterInput,
    ReactiveFormsModule,
    SubmitButton,
    HasScopeDirective,
  ],

  templateUrl: './partially-delivered-reasons-edit-component.html',

  styleUrl: './partially-delivered-reasons-edit-component.scss',
})
export class PartiallyDeliveredReasonsEditComponent implements OnInit {

  private router = inject(Router);
  private locale = inject(LOCALE_ID);
  private reasonService = inject(PartiallyDeliveredReasonsService);
  private destroyRef = inject(DestroyRef);
  readonly SCOPES = SCOPES;



  reasonId = signal<number | null>(null);

  titleArValue = signal('');

  titleEnValue = signal('');

  isEditable = signal(false);

  isArabic = computed(() => this.locale.startsWith('ar'));

  isLoading = signal(false);



  Loading = $localize`:@@partiallyDeliveredReasonLoading:Loading...`;

  titleArLabel = $localize`:@@partiallyDeliveredReasonEditTitleArabic:Reason Title (Arabic)`;

  titleArPlaceholder = $localize`:@@partiallyDeliveredReasonEditTitleArabicPlaceholder:Enter Arabic reason`;

  titleEnLabel = $localize`:@@partiallyDeliveredReasonEditTitleEnglish:Reason Title (English)`;

  titleEnPlaceholder = $localize`:@@partiallyDeliveredReasonEditTitleEnglishPlaceholder:Enter English reason (optional)`;

  cancelButton = $localize`:@@partiallyDeliveredReasonCancel:Cancel`;

  submitButton = $localize`:@@partiallyDeliveredReasonEdit:Edit`;

  confirmButton = signal($localize`:@@partiallyDeliveredReasonConfirm:Confirm`);

 

  showConfirmation = signal(false);

  confirmationTitle = $localize`:@@partiallyDeliveredReasonEditConfirmationTitle:Confirm Update`;

  confirmationDescription = $localize`:@@partiallyDeliveredReasonEditConfirmation:Are you sure you want to save these changes?`;



  errorMessage = signal('');

  successMessage = signal<string | null>(null);



  reasonForm = new FormGroup({
    titleAr: new FormControl('', [
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(255),
      arabicOnlyValidator(),
      noLeadingTrailingSpacesValidator(),
      noNumbersValidator(),
    ]),

    titleEn: new FormControl('', [
      Validators.minLength(3),
      Validators.maxLength(255),
      englishOnlyValidator(),
      noLeadingTrailingSpacesValidator(),
      noNumbersValidator(),
    ]),
  });



  readonly titleArControl = computed(() => this.reasonForm.controls.titleAr);

  readonly titleEnControl = computed(() => this.reasonForm.controls.titleEn);

  titleAr = computed(() => this.titleArControl().value ?? '');

  titleEn = computed(() => this.titleEnControl().value ?? '');

 

  ngOnInit(): void {
    const state = history.state;

    console.log('Edit Partially Delivered Reason State:', state);

    this.reasonId.set(state.id ?? null);

    this.titleArValue.set(state.titleAr ?? '');

    this.titleEnValue.set(state.titleEn ?? '');

    this.isEditable.set(state.isEditable ?? false);

    this.reasonForm.patchValue({
      titleAr: this.titleArValue(),

      titleEn: this.titleEnValue(),
    });
  }

 
  onArabicChange(value: string | number | null): void {
    const newValue = value?.toString() ?? '';

    this.titleArValue.set(newValue);

    this.titleArControl().setValue(newValue);

    this.titleArControl().markAsTouched();

    this.titleArControl().updateValueAndValidity();
  }



  onEnglishChange(value: string | number | null): void {
    const newValue = value?.toString() ?? '';

    this.titleEnValue.set(newValue);

    this.titleEnControl().setValue(newValue);

    this.titleEnControl().markAsTouched();

    this.titleEnControl().updateValueAndValidity();
  }


  submitForm(): void {
    this.reasonForm.markAllAsTouched();

    if (this.reasonForm.invalid) {
      return;
    }

    this.errorMessage.set('');

    this.showConfirmation.set(true);
  }



  cancel(): void {
    this.router.navigate(['/admin/partially-delivered-reasons']);
  }



  confirmEdit(): void {
    this.isLoading.set(true);

    this.confirmButton.set(this.Loading);

    const reasonId = this.reasonId();

    if (!reasonId) {
      this.isLoading.set(false);

      this.confirmButton.set($localize`:@@partiallyDeliveredReasonConfirm:Confirm`);

      this.errorMessage.set(this.isArabic() ? 'معرف السبب غير موجود.' : 'Reason ID is missing.');

      return;
    }

    const data = this.reasonForm.getRawValue();

    const dto: PartiallyDeliveredReasonDTO = {
      titleAr: data.titleAr ?? '',

      titleEn: optionalName(data.titleEn),
    };

    const subscription = this.reasonService.updateReason(reasonId, dto).subscribe({
      next: () => {
        const reasonName = displayName(this.isArabic(), dto.titleAr, dto.titleEn);

        this.successMessage.set(
          `${$localize`:@@partiallyDeliveredReasonUpdatedSuccessfully:Partially delivered reason`} "${reasonName}" ${$localize`:@@partiallyDeliveredReasonUpdatedSuccessfully2:updated successfully`}`,
        );

        this.showConfirmation.set(false);

        this.isLoading.set(false);

        this.confirmButton.set($localize`:@@partiallyDeliveredReasonConfirm:Confirm`);

        setTimeout(() => {
          this.router.navigate(['/admin/partially-delivered-reasons']);
        }, 3000);
      },

      error: (error) => {
        console.error('Error updating partially delivered reason:', error);

        this.isLoading.set(false);

        this.confirmButton.set($localize`:@@partiallyDeliveredReasonConfirm:Confirm`);

        if (error.code === 'REST-0004' && error.reqBodyErrors) {
          const hasArabicError = error.reqBodyErrors.some((error: string) => error.includes('Field: titleAr'));

          const hasEnglishError = error.reqBodyErrors.some((error: string) => error.includes('Field: titleEn'));

          if (hasArabicError && hasEnglishError) {
            this.errorMessage.set(
              this.isArabic()
                ? 'اسم السبب باللغة العربية والإنجليزية غير صالح.'
                : 'The Arabic and English reason names are invalid.',
            );

            return;
          }

          if (hasArabicError) {
            this.errorMessage.set(
              this.isArabic() ? 'اسم السبب باللغة العربية غير صالح.' : 'The Arabic reason name is invalid.',
            );

            return;
          }

          if (hasEnglishError) {
            this.errorMessage.set(
              this.isArabic() ? 'اسم السبب باللغة الإنجليزية غير صالح.' : 'The English reason name is invalid.',
            );

            return;
          }
        }

        if (error.code === 'REST-0001') {
          this.errorMessage.set(this.isArabic() ? 'حدث خطأ ما.' : 'Something went wrong.');

          return;
        }

        this.errorMessage.set(
          this.isArabic() ? error.messageAr || error.messageEn : error.messageEn || error.messageAr,
        );
      },
    });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }


  closeConfirmation(): void {
    this.showConfirmation.set(false);
  }
}
